"use client"

import { useCallback, useEffect, useMemo, useState } from "react"
import { m, AnimatePresence } from "framer-motion"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { useCountry } from "@/components/layout/country-provider"
import Link from "next/link"
import { cn } from "@/lib/utils"
import { useLocalePath } from "@/lib/use-localized-navigation"
import { cmsLocale } from "@/lib/cms"
import { useEditRegion } from "@/lib/edit/edit-region-registry"
import { EditableField } from "@/components/editor/editable-field"
import { createPageSearcher } from "@/lib/search/page-filter"
import { CategoryFilter } from "@/components/ui/category-filter"
import { PACKAGE_FILTER_ORDER } from "@/lib/categories"
import {
  getSymptomGroupMeta,
  type SymptomGroupMeta,
} from "@/lib/symptom-groups"
import { getSymptomGroupIcon } from "@/lib/category-icons"
import {
  ChevronDownIcon,
  ChevronRightSmIcon,
  WarningIcon,
  SearchIcon,
  CloseIcon,
} from "@/components/ui/icons"
import type { CmsSymptom, CmsSymptomGroup } from "@/lib/cms"

interface SymptomsExplorerProps {
  groups: CmsSymptomGroup[]
  symptoms: CmsSymptom[]
  /** symptomSlug → the test/package ids that cover it (union of its related
   *  biomarkers/diseases' packages). Drives the test filter chips. */
  symptomPackages: Record<string, string[]>
  locale: string
  /** Rendered inline inside the biomarker/disease explorer tab rather than as
   *  the standalone `/symptoms` page. Switches to the explorer's rail layout
   *  (group list + active-group rows, no test filter), scoped to `scopePackages`. */
  embedded?: boolean
  /** Test packages of the page the embedded explorer sits on — symptoms are
   *  scoped to those covered by the test, matching the biomarker/disease tabs. */
  scopePackages?: string[]
}

// ─── Derived shapes ──────────────────────────────────────────────────────────

interface RelatedItem {
  relationTo: "biomarkers" | "diseases"
  slug: string
  name: string
  why?: string | null
}

function groupSlugOf(symptom: CmsSymptom): string | undefined {
  const g = symptom.group
  if (!g) return undefined
  if (typeof g === "string" || typeof g === "number") return undefined
  return g.slug
}

/** Split a symptom's curated keyRelations into biomarker vs condition tags,
 *  dropping rows without a resolvable slug/name. */
function relatedItemsOf(
  symptom: CmsSymptom,
  linkableDiseaseSlugs?: ReadonlySet<string>
): {
  biomarkers: RelatedItem[]
  conditions: RelatedItem[]
} {
  const biomarkers: RelatedItem[] = []
  const conditions: RelatedItem[] = []
  for (const rel of symptom.keyRelations ?? []) {
    const item = rel.item
    if (!item) continue
    const value = item.value
    if (typeof value === "string" || typeof value === "number") continue
    const slug = value.slug
    const name = value.name
    if (!slug || !name) continue
    if (
      item.relationTo === "diseases" &&
      linkableDiseaseSlugs &&
      !linkableDiseaseSlugs.has(slug)
    )
      continue
    const entry: RelatedItem = {
      relationTo: item.relationTo,
      slug,
      name,
      why: rel.why,
    }
    if (item.relationTo === "biomarkers") biomarkers.push(entry)
    else conditions.push(entry)
  }
  return { biomarkers, conditions }
}

function synonymValues(symptom: CmsSymptom): string[] {
  return (symptom.synonyms ?? [])
    .map((s) => s.value)
    .filter((v): v is string => typeof v === "string" && v.length > 0)
}

// ─── Sub-components ──────────────────────────────────────────────────────────

function GroupBadge({
  meta,
  label,
}: {
  meta: SymptomGroupMeta | undefined
  label: string
}) {
  const slug = meta?.slug ?? ""
  const Icon = getSymptomGroupIcon(slug)
  return (
    <span
      className={cn(
        "type-label-sm inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium",
        meta?.color ?? "bg-neutral-100",
        meta?.textColor ?? "text-neutral-700"
      )}
    >
      <Icon width={13} height={13} />
      {label}
    </span>
  )
}

/** An expandable symptom row inside a group section — mirrors the
 *  biomarker/disease list rows: name + chevron, expanding to the description
 *  and a link to the canonical `/symptoms/<slug>` detail page. */
export function SymptomRow({
  symptom,
  expanded,
  onToggle,
  href,
  moreLabel,
  editLocale,
}: {
  symptom: CmsSymptom
  expanded: boolean
  onToggle: () => void
  href: string
  moreLabel: string
  /** CMS locale these docs were fetched in (region provenance). */
  editLocale: string
}) {
  const t = useTranslations("symptoms")
  return (
    <div className="lazy-row border-b border-neutral-100">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="hover:text-aubergine-900 group flex w-full cursor-pointer items-center justify-between gap-3 py-2.5 text-left text-base text-neutral-700 transition-colors"
      >
        <span className="flex items-center gap-2">
          {symptom.name}
          {symptom.redFlag ? (
            <span
              title={t("redFlagTooltip")}
              aria-label={t("redFlagTooltip")}
              className="inline-flex cursor-help"
            >
              <WarningIcon
                width={14}
                height={14}
                className="text-terracotta-300 shrink-0"
              />
            </span>
          ) : null}
        </span>
        <ChevronDownIcon
          width={16}
          height={16}
          className={cn(
            "group-hover:text-aubergine-500 shrink-0 text-neutral-300 transition-all duration-300",
            expanded && "text-aubergine-500 rotate-180"
          )}
        />
      </button>
      {/* safe-animate-presence: conditional-mount — gated by `expanded` */}
      <AnimatePresence>
        {expanded && (
          <m.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: [0.25, 1, 0.5, 1] }}
            className="overflow-hidden"
          >
            <div className="pr-8 pb-3">
              {symptom.description ? (
                <EditableField
                  collection="symptoms"
                  id={symptom.id}
                  field="description"
                  locale={editLocale}
                  kind="scalar"
                  as="p"
                  className="type-body-md leading-relaxed text-neutral-500"
                >
                  {symptom.description}
                </EditableField>
              ) : null}
              <Link
                href={href}
                className="text-aubergine-700 hover:text-aubergine-900 mt-2 inline-flex items-center gap-1 text-sm font-medium transition-colors"
              >
                {moreLabel}
                <ChevronRightSmIcon />
              </Link>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </div>
  )
}

function TagGroup({
  testId,
  heading,
  dotClass,
  items,
  hrefFor,
}: {
  testId: string
  heading: string
  dotClass: string
  items: RelatedItem[]
  hrefFor: (item: RelatedItem) => string
}) {
  const t = useTranslations("symptoms")
  const [openSlugs, setOpenSlugs] = useState<ReadonlySet<string>>(new Set())
  if (items.length === 0) return null
  return (
    <div data-testid={testId}>
      <h3 className="type-label-md mb-2 text-neutral-400">{heading}</h3>
      <ul className="flex flex-wrap gap-2">
        {items.map((item) => {
          const isOpen = openSlugs.has(item.slug)
          return (
            <li key={`${item.relationTo}-${item.slug}`} className="max-w-full">
              <span className="inline-flex flex-col">
                <span className="inline-flex items-center gap-1">
                  <Link
                    href={hrefFor(item)}
                    className="type-body-sm inline-flex items-center gap-1.5 rounded-full bg-white px-3 py-1.5 font-medium text-neutral-800 transition-colors hover:bg-neutral-100"
                  >
                    <span
                      aria-hidden
                      className={cn("size-2 shrink-0 rounded-full", dotClass)}
                    />
                    {item.name}
                  </Link>
                  {item.why ? (
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      aria-label={t("whyRelates")}
                      onClick={() =>
                        setOpenSlugs((prev) => {
                          const next = new Set(prev)
                          if (next.has(item.slug)) next.delete(item.slug)
                          else next.add(item.slug)
                          return next
                        })
                      }
                      className="flex size-6 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-700"
                    >
                      <ChevronDownIcon
                        width={14}
                        height={14}
                        className={cn(
                          "transition-transform duration-200",
                          isOpen && "rotate-180"
                        )}
                      />
                    </button>
                  ) : null}
                </span>
                {item.why && isOpen ? (
                  <span className="type-body-sm mt-1 max-w-[280px] px-1 text-neutral-500">
                    {item.why}
                  </span>
                ) : null}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/** Symptom detail content column, rendered by the `/symptoms/[slug]` route
 *  inside the shared two-column shell (sticky check-ups sidebar lives in the
 *  route, mirroring the biomarker/disease detail pages). Keeps the flowing
 *  tag-cloud body — the right fit for the uneven biomarker vs condition
 *  lists. */
export function SymptomDetail({
  symptom,
  groupName,
  linkableDiseaseSlugs,
}: {
  symptom: CmsSymptom
  groupName: string
  linkableDiseaseSlugs?: ReadonlySet<string>
}) {
  const t = useTranslations("symptoms")
  const lp = useLocalePath()
  // CMS locale these docs were fetched in — the inline editor stamps it so a
  // draft write targets the matching Payload locale row. No-op in public prod.
  const locale = useLocale()
  const editLocale = cmsLocale(locale, useCountry().code)
  const descriptionRef = useEditRegion<HTMLParagraphElement>(
    symptom.description
      ? {
          collection: "symptoms",
          id: symptom.id,
          field: "description",
          locale: editLocale,
          kind: "scalar",
        }
      : null
  )
  const meta = getSymptomGroupMeta(groupSlugOf(symptom) ?? "")
  const { biomarkers, conditions } = relatedItemsOf(
    symptom,
    linkableDiseaseSlugs
  )
  const synonyms = synonymValues(symptom)
  const relatedArticles = (symptom.relatedArticles ?? []).filter(
    (a): a is { slug?: string; title?: string } =>
      typeof a === "object" && a !== null
  )

  return (
    <div data-testid="symptom-detail" className="flex flex-col gap-8">
      <header className="flex flex-col gap-3">
        <GroupBadge meta={meta} label={groupName} />
        <h1 className="type-display-lg text-neutral-900">{symptom.name}</h1>
        {synonyms.length > 0 ? (
          <p className="type-body-md text-neutral-500">
            {t("synonymsPrefix", { list: synonyms.join(", ") })}
          </p>
        ) : null}
        <p
          ref={descriptionRef}
          className="type-body-lg max-w-2xl text-neutral-700"
        >
          {symptom.description || t("detailIntro")}
        </p>
      </header>

      {symptom.redFlag ? (
        <div
          data-testid="red-flag-banner"
          className="rounded-[16px] bg-amber-50 p-5 text-amber-900"
        >
          <p className="type-label-md mb-1 font-medium text-amber-800">
            {t("urgentHeading")}
          </p>
          <p className="type-body-md">{t("redFlagNote")}</p>
        </div>
      ) : null}

      <section className="flex flex-col gap-5">
        <div className="flex flex-col gap-1">
          <h2 className="type-title-lg font-medium text-neutral-900">
            {t("whatRelatesHeading")}
          </h2>
          <p className="type-body-sm text-neutral-500">{t("whatRelatesSub")}</p>
        </div>
        <div className="flex flex-col gap-6">
          <TagGroup
            testId="biomarker-tag-group"
            heading={t("relatedBiomarkers")}
            dotClass="bg-teal-500"
            items={biomarkers}
            hrefFor={(item) => lp(`/biomarkers/${item.slug}`)}
          />
          <TagGroup
            testId="condition-tag-group"
            heading={t("relatedConditions")}
            dotClass="bg-rose-500"
            items={conditions}
            hrefFor={(item) => lp(`/what-we-detect/${item.slug}`)}
          />
        </div>
        {/* Legend */}
        <div className="type-body-sm flex flex-wrap gap-4 text-neutral-500">
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-teal-500" aria-hidden />
            {t("legendBiomarker")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="size-2 rounded-full bg-rose-500" aria-hidden />
            {t("legendCondition")}
          </span>
        </div>
      </section>

      {relatedArticles.length > 0 ? (
        <section className="flex flex-col gap-3">
          <h2 className="type-label-lg text-neutral-400">
            {t("relatedReading")}
          </h2>
          <ul className="flex flex-col gap-1 rounded-[16px] bg-white p-2">
            {relatedArticles.map((a) => (
              <li key={a.slug}>
                <Link
                  href={lp(`/insights/${a.slug ?? ""}`)}
                  className="type-body-lg block rounded-[12px] p-3 font-medium text-neutral-900 transition-colors hover:bg-neutral-50"
                >
                  {a.title}
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  )
}

// ─── Root ────────────────────────────────────────────────────────────────────

export function SymptomsExplorer({
  groups,
  symptoms,
  symptomPackages,
  locale,
  embedded = false,
  scopePackages,
}: SymptomsExplorerProps) {
  const t = useTranslations("symptoms")
  const wt = useTranslations("whatWeDetect")
  const ut = useTranslations("ui")
  const rt = useTranslations("regulatory")
  const lp = useLocalePath()
  // CMS locale these docs were fetched in — the inline editor stamps it so a
  // draft write targets the matching Payload locale row. No-op in public prod.
  const editLocale = cmsLocale(locale, useCountry().code)
  const [activePackage, setActivePackage] = useState<string | null>(null)
  const [search, setSearch] = useState("")
  const [expandedSlug, setExpandedSlug] = useState<string | null>(null)
  const [activeGroupIdx, setActiveGroupIdx] = useState(0)

  const sortedGroups = useMemo(
    () => [...groups].sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [groups]
  )

  // The list payload is depth=0, so a symptom's `group` is a bare id (the
  // relationship isn't populated). Resolve it to a slug via the separately
  // fetched groups list so we can bucket symptoms under their group section.
  const groupSlugById = useMemo(() => {
    const map = new Map<string, string>()
    for (const g of groups) map.set(String(g.id), g.slug)
    return map
  }, [groups])

  const resolveGroupSlug = useCallback(
    (s: CmsSymptom): string | undefined => {
      const g = s.group
      if (g == null) return undefined
      if (typeof g === "object")
        return g.slug ?? groupSlugById.get(String(g.id))
      return groupSlugById.get(String(g))
    },
    [groupSlugById]
  )

  const searcher = useMemo(
    () =>
      createPageSearcher(symptoms, locale, {
        augmentAliases: (s) => synonymValues(s),
      }),
    [symptoms, locale]
  )

  // Test (package) filter chips — the same primary filter the biomarker/disease
  // pages use, built from the tests that actually cover at least one symptom.
  const packageFilters = useMemo(() => {
    const available = new Set<string>()
    for (const pkgs of Object.values(symptomPackages))
      for (const p of pkgs) available.add(p)
    return PACKAGE_FILTER_ORDER.filter((p) => available.has(p.id)).map((p) => ({
      id: p.id,
      label: wt(p.translationKey as never),
    }))
  }, [symptomPackages, wt])

  // Embedded on a test page: scope to symptoms the test actually covers, so the
  // tab mirrors the test-scoped Health risks / Biomarkers tabs.
  const scopedSymptoms = useMemo(() => {
    if (!scopePackages?.length) return symptoms
    return symptoms.filter((s) =>
      (symptomPackages[s.slug] ?? []).some((p) => scopePackages.includes(p))
    )
  }, [scopePackages, symptoms, symptomPackages])

  const base = embedded ? scopedSymptoms : symptoms
  const baseSlugs = useMemo(() => new Set(base.map((s) => s.slug)), [base])

  const visible = useMemo(() => {
    let list = search.trim()
      ? searcher.filter(search).filter((s) => baseSlugs.has(s.slug))
      : base
    if (!embedded && activePackage)
      list = list.filter((s) =>
        (symptomPackages[s.slug] ?? []).includes(activePackage)
      )
    return list
  }, [
    base,
    baseSlugs,
    searcher,
    search,
    embedded,
    activePackage,
    symptomPackages,
  ])

  // Bucket the visible symptoms under their group, preserving group order.
  const byGroup = useMemo(() => {
    const map = new Map<string, CmsSymptom[]>()
    for (const s of visible) {
      const slug = resolveGroupSlug(s)
      if (!slug) continue
      const arr = map.get(slug) ?? []
      arr.push(s)
      map.set(slug, arr)
    }
    return map
  }, [visible, resolveGroupSlug])

  // Scroll to a group section when arriving from a group link (e.g. the
  // detail-page breadcrumb → `/symptoms?group=<slug>`). Read post-mount so the
  // static page stays cacheable; skipped when embedded on another page.
  useEffect(() => {
    if (embedded) return
    const g = new URLSearchParams(window.location.search).get("group")
    if (g)
      document
        .getElementById(`sym-group-${g}`)
        ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [embedded])

  // Groups that have visible symptoms, in display order.
  const groupsWithItems = useMemo(
    () =>
      sortedGroups
        .map((g) => ({ group: g, items: byGroup.get(g.slug) ?? [] }))
        .filter((x) => x.items.length > 0),
    [sortedGroups, byGroup]
  )

  const renderRow = (s: CmsSymptom) => (
    <SymptomRow
      key={s.slug}
      symptom={s}
      expanded={expandedSlug === s.slug}
      onToggle={() => setExpandedSlug(expandedSlug === s.slug ? null : s.slug)}
      href={lp(`/symptoms/${s.slug}`)}
      moreLabel={`${ut("moreAbout")} ${s.name}`}
      editLocale={editLocale}
    />
  )

  // ─── Embedded (test page): explorer-style rail, scoped to the test ─────────
  // Mirrors the Health risks / Biomarkers tabs — left-rail group selector +
  // active-group rows — instead of the standalone test-filter layout.
  if (embedded) {
    const activeIdx = Math.min(
      activeGroupIdx,
      Math.max(0, groupsWithItems.length - 1)
    )
    const active = groupsWithItems[activeIdx]
    const nextIdx =
      groupsWithItems.length > 1 ? (activeIdx + 1) % groupsWithItems.length : -1
    return (
      <div className="grid w-full grid-cols-1 gap-6 md:grid-cols-[280px_1fr] md:gap-8">
        {/* LEFT — search pill + group list */}
        <div className="flex w-full flex-col items-start gap-4 md:sticky md:top-20 md:self-start">
          <ul className="flex w-full flex-wrap items-start gap-x-4 gap-y-1 md:flex-col md:gap-1.5">
            <li className="w-full max-w-full md:mb-2">
              <label className="flex w-full max-w-full cursor-text items-center gap-3 rounded-[28px] bg-[rgba(64,64,64,0.05)] px-4 py-3 transition-colors focus-within:bg-white focus-within:ring-1 focus-within:ring-neutral-300 hover:bg-[rgba(64,64,64,0.09)] md:w-3/4">
                {" "}
                {/* ds-color-ignore: translucent overlay (alpha) */}
                <span className="flex size-5 shrink-0 items-center justify-center text-neutral-500">
                  <SearchIcon width={20} height={20} />
                </span>
                <input
                  type="search"
                  aria-label={t("searchPlaceholder")}
                  autoComplete="off"
                  placeholder={t("searchPlaceholder")}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="type-title-sm md:type-title-md w-full min-w-0 bg-transparent text-neutral-900 outline-none placeholder:font-normal placeholder:text-neutral-400 [&::-webkit-search-cancel-button]:appearance-none"
                />
                {search ? (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    aria-label={ut("clearSearch")}
                    className="-mr-1 flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-full text-neutral-500 transition-colors hover:bg-neutral-200/60 hover:text-neutral-900"
                  >
                    <CloseIcon width={18} height={18} />
                  </button>
                ) : null}
              </label>
            </li>
            {groupsWithItems.map(({ group, items }, idx) => {
              const Icon = getSymptomGroupIcon(group.slug)
              const isActive = idx === activeIdx
              return (
                <li key={group.slug} className="max-w-full md:w-full">
                  <button
                    type="button"
                    onClick={() => setActiveGroupIdx(idx)}
                    aria-current={isActive ? "true" : undefined}
                    className={cn(
                      "group flex w-fit max-w-full cursor-pointer items-center gap-3 rounded-full px-4 py-2.5 text-left text-neutral-900 transition-colors",
                      isActive ? "bg-white" : "hover:bg-[rgba(64,64,64,0.05)]" // ds-color-ignore: translucent overlay (alpha)
                    )}
                  >
                    <span className="hidden size-6 shrink-0 md:block">
                      <Icon width={24} height={24} />
                    </span>
                    <span className="flex items-baseline gap-2">
                      <span className="type-title-md">{group.name}</span>
                      <span
                        aria-hidden="true"
                        className="type-title-md hidden font-normal text-neutral-400 md:inline"
                      >
                        {items.length}
                      </span>
                    </span>
                  </button>
                </li>
              )
            })}
          </ul>
        </div>

        {/* RIGHT — active group's symptoms */}
        <div className="w-full">
          {active ? (
            <>
              <div className="mb-5 flex items-center gap-2.5">
                <h3 className="type-title-lg leading-[130%] text-neutral-900">
                  {active.group.name}
                </h3>
                <span className="type-body-sm text-neutral-400">
                  {t("symptomCount", { count: active.items.length })}
                </span>
              </div>
              <div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
                {active.items.map(renderRow)}
              </div>
              {nextIdx >= 0 ? (
                <div className="mt-8 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setActiveGroupIdx(nextIdx)}
                    className="group inline-flex cursor-pointer items-center gap-2 rounded-full border border-neutral-100 bg-white px-5 py-3 text-neutral-600 shadow-[0_2px_11px_rgba(0,0,0,0.04)] transition-colors hover:border-neutral-300 hover:text-neutral-900"
                  >
                    <span className="type-title-sm">
                      {t("nextGroup")}: {groupsWithItems[nextIdx].group.name}
                    </span>
                    <ChevronRightSmIcon className="size-4 transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>
              ) : null}
            </>
          ) : (
            <p className="type-body-lg text-neutral-500">{t("noResults")}</p>
          )}
        </div>
      </div>
    )
  }

  // ─── Standalone (/symptoms): sibling hero + tab bar + test filter ──────────
  const pillBase =
    "type-title-sm rounded-full px-5 py-2.5 transition-all duration-200"
  return (
    <>
      <section className="pt-12 pb-6 text-center md:pt-16">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          <p className="type-label-lg text-neutral-400">{wt("label")}</p>
          <h1 className="type-display-lg mx-auto mt-4 max-w-3xl text-balance text-neutral-900">
            {t.rich("heading", {
              highlight: (chunks) => (
                <span className="text-bright-green-600">{chunks}</span>
              ),
            })}
          </h1>
          <p className="type-body-lg mx-auto mt-4 max-w-2xl text-neutral-600">
            {t("pageIntro")}
          </p>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-neutral-400 italic">
            {rt("disclaimer")}
          </p>
          <div className="mt-8">
            <div className="inline-flex rounded-full bg-white p-1 shadow-sm">
              <Link
                href={lp("/what-we-detect")}
                prefetch={true}
                className={cn(
                  pillBase,
                  "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                )}
              >
                {wt("tabHealthRisks")}
              </Link>
              <Link
                href={lp("/biomarkers")}
                prefetch={true}
                className={cn(
                  pillBase,
                  "text-neutral-500 hover:bg-neutral-100 hover:text-neutral-700"
                )}
              >
                {wt("tabBiomarkers")}
              </Link>
              <span className={cn(pillBase, "bg-aubergine-800 text-white")}>
                {t("tabLabel")}
              </span>
            </div>
          </div>
        </div>
      </section>

      <CategoryFilter
        packages={packageFilters}
        activePackage={activePackage}
        onSelectPackage={setActivePackage}
        searchQuery={search}
        onSearchChange={setSearch}
        resultCount={visible.length}
        allLabel={wt("filterAll")}
        searchPlaceholder={t("searchPlaceholder")}
        resultLabel={t("symptomCount", { count: visible.length })}
        addonLabel={wt("filterAddons")}
      />

      <section className="mt-6 pb-20">
        <div className="mx-auto max-w-[1440px] px-4 md:px-10">
          {groupsWithItems.length > 0 ? (
            <div className="flex flex-col gap-12">
              {groupsWithItems.map(({ group, items }) => {
                const Icon = getSymptomGroupIcon(group.slug)
                return (
                  <div
                    key={group.slug}
                    id={`sym-group-${group.slug}`}
                    className="scroll-mt-40"
                  >
                    <div className="mb-5 flex items-center gap-2.5 border-b border-neutral-200 pb-3">
                      <Icon
                        width={18}
                        height={18}
                        className="text-neutral-500"
                      />
                      <h2 className="type-title-lg leading-[130%] text-neutral-900">
                        {group.name}
                      </h2>
                      <span className="type-body-sm text-neutral-400">
                        {t("symptomCount", { count: items.length })}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-x-8 gap-y-1 md:grid-cols-2">
                      {items.map(renderRow)}
                    </div>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="type-body-lg text-neutral-500">{t("noResults")}</p>
          )}
        </div>
      </section>
    </>
  )
}
